import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, addDays, isAfter, isBefore, parseISO } from "date-fns";
import { Search, X, SlidersHorizontal, ArrowUpDown, Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

/**
 * PNRFilters
 *
 * Drop-in filter + sort bar for the PNR list.
 * Reads/writes filter state to URL query string so refreshes preserve filters.
 *
 * Usage:
 *   const [filteredPnrs, setFilteredPnrs] = useState([]);
 *   <PNRFilters pnrs={pnrs} onFilteredChange={setFilteredPnrs} />
 *   {filteredPnrs.map(...)}
 */

const DATE_PRESETS = [
  { value: "all", label: "All dates" },
  { value: "today", label: "Today" },
  { value: "week", label: "This week" },
  { value: "upcoming", label: "Upcoming" },
  { value: "past", label: "Past" },
  { value: "custom", label: "Custom range" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "confirmed", label: "Confirmed (CNF)" },
  { value: "rac", label: "RAC" },
  { value: "waiting", label: "Waitlist (WL)" },
  { value: "cancelled", label: "Cancelled" },
];

const CLASS_OPTIONS = [
  { value: "all", label: "All classes" },
  { value: "SL", label: "Sleeper (SL)" },
  { value: "3A", label: "3rd AC (3A)" },
  { value: "2A", label: "2nd AC (2A)" },
  { value: "1A", label: "1st AC (1A)" },
  { value: "CC", label: "Chair Car (CC)" },
  { value: "EC", label: "Exec. Chair (EC)" },
  { value: "2S", label: "2nd Sitting (2S)" },
];

const SORT_OPTIONS = [
  { value: "journey-asc", label: "Journey date (earliest first)" },
  { value: "journey-desc", label: "Journey date (latest first)" },
  { value: "added-desc", label: "Recently added" },
  { value: "added-asc", label: "Oldest added" },
  { value: "probability-desc", label: "Confirmation % (high → low)" },
  { value: "probability-asc", label: "Confirmation % (low → high)" },
];

// Map status filter to substring matchers on passenger.current_status
const matchesStatus = (pnr, statusFilter) => {
  if (statusFilter === "all") return true;
  const statuses = (pnr.passengers || []).map((p) => (p.current_status || "").toUpperCase());

  switch (statusFilter) {
    case "confirmed":
      return statuses.some((s) => s.includes("CNF") || s.includes("CONFIRMED"));
    case "rac":
      return statuses.some((s) => s.includes("RAC"));
    case "waiting":
      return statuses.some(
        (s) =>
          s.includes("WL") ||
          s.includes("WAITING") ||
          s.includes("RLWL") ||
          s.includes("GNWL") ||
          s.includes("PQWL") ||
          s.includes("RSWL") ||
          s.includes("TQWL")
      );
    case "cancelled":
      return statuses.some((s) => s.includes("CAN") || s.includes("CANCELLED"));
    default:
      return true;
  }
};

const matchesDate = (pnr, dateFilter, customFrom, customTo) => {
  if (dateFilter === "all") return true;
  if (!pnr.journey_date) return false;

  let journeyDate;
  try {
    journeyDate = parseISO(pnr.journey_date);
    if (isNaN(journeyDate.getTime())) return false;
  } catch {
    return false;
  }

  const now = new Date();
  const today = startOfDay(now);

  switch (dateFilter) {
    case "today":
      return (
        journeyDate >= today && journeyDate < addDays(today, 1)
      );
    case "week": {
      const weekStart = startOfWeek(now, { weekStartsOn: 1 }); // Monday
      const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
      return journeyDate >= weekStart && journeyDate <= weekEnd;
    }
    case "upcoming":
      return journeyDate >= today;
    case "past":
      return journeyDate < today;
    case "custom": {
      if (customFrom && isBefore(journeyDate, startOfDay(customFrom))) return false;
      if (customTo && isAfter(journeyDate, endOfDay(customTo))) return false;
      return true;
    }
    default:
      return true;
  }
};

const matchesClass = (pnr, classFilter) => {
  if (classFilter === "all") return true;
  return (pnr.journey_class || "").toUpperCase() === classFilter.toUpperCase();
};

const matchesSearch = (pnr, query) => {
  if (!query) return true;
  const q = query.toLowerCase().trim();
  if (!q) return true;
  const haystack = [
    pnr.pnr_number,
    pnr.train_number,
    pnr.train_name,
    pnr.boarding_station,
    pnr.boarding_station_name,
    pnr.destination_station,
    pnr.destination_station_name,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
};

const sortPnrs = (pnrs, sort) => {
  const sorted = [...pnrs];
  switch (sort) {
    case "journey-asc":
      return sorted.sort((a, b) => (a.journey_date || "").localeCompare(b.journey_date || ""));
    case "journey-desc":
      return sorted.sort((a, b) => (b.journey_date || "").localeCompare(a.journey_date || ""));
    case "added-desc":
      return sorted.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    case "added-asc":
      return sorted.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    case "probability-desc":
      return sorted.sort((a, b) => (b.confirmation_probability || 0) - (a.confirmation_probability || 0));
    case "probability-asc":
      return sorted.sort((a, b) => (a.confirmation_probability || 0) - (b.confirmation_probability || 0));
    default:
      return sorted;
  }
};

const PNRFilters = ({ pnrs = [], onFilteredChange }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL
  const [dateFilter, setDateFilter] = useState(searchParams.get("date") || "all");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [classFilter, setClassFilter] = useState(searchParams.get("class") || "all");
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);
  const [sort, setSort] = useState(searchParams.get("sort") || "journey-asc");
  const [customFrom, setCustomFrom] = useState(
    searchParams.get("from") ? parseISO(searchParams.get("from")) : null
  );
  const [customTo, setCustomTo] = useState(
    searchParams.get("to") ? parseISO(searchParams.get("to")) : null
  );

  // Debounce search input (300ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Sync filter state to URL (debounced for search).
  //
  // CRITICAL: use the functional updater so unrelated params (notably
  // the Pro Dashboard's ?view= from Dashboard.jsx handleViewChange)
  // are preserved. Passing a plain object here would wipe the entire
  // query string and snap the sidebar back to mission-control on every
  // mount / re-render of this component.
  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      // Clear our own filter keys first — handles the "filter turned
      // off" case (e.g. user clears a search) where we need to remove
      // the param entirely, not leave the previous value behind.
      ["date", "status", "class", "q", "sort", "from", "to"].forEach(k => next.delete(k));
      // Re-add with the current filter state.
      if (dateFilter !== "all") next.set("date", dateFilter);
      if (statusFilter !== "all") next.set("status", statusFilter);
      if (classFilter !== "all") next.set("class", classFilter);
      if (debouncedQuery) next.set("q", debouncedQuery);
      if (sort !== "journey-asc") next.set("sort", sort);
      if (dateFilter === "custom" && customFrom) next.set("from", format(customFrom, "yyyy-MM-dd"));
      if (dateFilter === "custom" && customTo) next.set("to", format(customTo, "yyyy-MM-dd"));
      return next;
    }, { replace: true });
  }, [dateFilter, statusFilter, classFilter, debouncedQuery, sort, customFrom, customTo, setSearchParams]);

  // Compute filtered + sorted list
  const filteredPnrs = useMemo(() => {
    const filtered = pnrs.filter(
      (pnr) =>
        matchesDate(pnr, dateFilter, customFrom, customTo) &&
        matchesStatus(pnr, statusFilter) &&
        matchesClass(pnr, classFilter) &&
        matchesSearch(pnr, debouncedQuery)
    );
    return sortPnrs(filtered, sort);
  }, [pnrs, dateFilter, statusFilter, classFilter, debouncedQuery, sort, customFrom, customTo]);

  // Notify parent
  useEffect(() => {
    if (onFilteredChange) onFilteredChange(filteredPnrs);
  }, [filteredPnrs, onFilteredChange]);

  const hasActiveFilters =
    dateFilter !== "all" ||
    statusFilter !== "all" ||
    classFilter !== "all" ||
    debouncedQuery !== "" ||
    sort !== "journey-asc";

  const handleClearAll = useCallback(() => {
    setDateFilter("all");
    setStatusFilter("all");
    setClassFilter("all");
    setSearchQuery("");
    setSort("journey-asc");
    setCustomFrom(null);
    setCustomTo(null);
  }, []);

  // Don't render anything if there are no PNRs at all
  if (pnrs.length === 0) return null;

  return (
    <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-lg p-4 mb-6 space-y-3">
      <div className="flex items-center gap-2 text-zinc-300">
        <SlidersHorizontal className="w-4 h-4" />
        <span className="text-sm font-medium">Filter & Sort</span>
        <span className="text-xs text-zinc-500 ml-auto">
          Showing <span className="text-zinc-200 font-medium">{filteredPnrs.length}</span> of{" "}
          <span className="text-zinc-200 font-medium">{pnrs.length}</span>
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Search */}
        <div className="relative lg:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            type="text"
            placeholder="Search PNR, train name, station..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-zinc-900 border-zinc-800 text-zinc-50 placeholder:text-zinc-500 pl-9 pr-9"
            data-testid="filter-search"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Date filter */}
        <Select value={dateFilter} onValueChange={setDateFilter}>
          <SelectTrigger
            className="bg-zinc-900 border-zinc-800 text-zinc-50"
            data-testid="filter-date"
          >
            <SelectValue placeholder="Date" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            {DATE_PRESETS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} className="text-zinc-200">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Status filter */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger
            className="bg-zinc-900 border-zinc-800 text-zinc-50"
            data-testid="filter-status"
          >
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} className="text-zinc-200">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Class filter */}
        <Select value={classFilter} onValueChange={setClassFilter}>
          <SelectTrigger
            className="bg-zinc-900 border-zinc-800 text-zinc-50"
            data-testid="filter-class"
          >
            <SelectValue placeholder="Class" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            {CLASS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} className="text-zinc-200">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Sort */}
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger
            className="bg-zinc-900 border-zinc-800 text-zinc-50 lg:col-span-2"
            data-testid="filter-sort"
          >
            <ArrowUpDown className="w-4 h-4 mr-2 text-zinc-500" />
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            {SORT_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} className="text-zinc-200">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Clear all */}
        <Button
          variant="ghost"
          onClick={handleClearAll}
          disabled={!hasActiveFilters}
          className="text-zinc-400 hover:text-zinc-50 disabled:opacity-30"
          data-testid="filter-clear"
        >
          <X className="w-4 h-4 mr-2" />
          Clear all
        </Button>
      </div>

      {/* Custom date range pickers (shown only when 'custom' selected) */}
      {dateFilter === "custom" && (
        <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-zinc-800/50">
          <div className="flex-1">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start bg-zinc-900 border-zinc-800 text-zinc-50 hover:bg-zinc-800"
                  data-testid="filter-from-date"
                >
                  <CalendarIcon className="w-4 h-4 mr-2 text-zinc-500" />
                  {customFrom ? format(customFrom, "PPP") : "From date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800" align="start">
                <Calendar
                  mode="single"
                  selected={customFrom}
                  onSelect={setCustomFrom}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex-1">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start bg-zinc-900 border-zinc-800 text-zinc-50 hover:bg-zinc-800"
                  data-testid="filter-to-date"
                >
                  <CalendarIcon className="w-4 h-4 mr-2 text-zinc-500" />
                  {customTo ? format(customTo, "PPP") : "To date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800" align="start">
                <Calendar
                  mode="single"
                  selected={customTo}
                  onSelect={setCustomTo}
                  disabled={(date) => (customFrom ? isBefore(date, customFrom) : false)}
                />
              </PopoverContent>
            </Popover>
          </div>
          {(customFrom || customTo) && (
            <Button
              variant="ghost"
              onClick={() => {
                setCustomFrom(null);
                setCustomTo(null);
              }}
              className="text-zinc-400"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      )}

      {/* Active filter badges */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-zinc-800/50">
          <span className="text-xs text-zinc-500">Active:</span>
          {dateFilter !== "all" && (
            <Badge variant="outline" className="bg-indigo-500/10 text-indigo-300 border-indigo-500/30">
              {DATE_PRESETS.find((d) => d.value === dateFilter)?.label}
            </Badge>
          )}
          {statusFilter !== "all" && (
            <Badge variant="outline" className="bg-indigo-500/10 text-indigo-300 border-indigo-500/30">
              {STATUS_OPTIONS.find((d) => d.value === statusFilter)?.label}
            </Badge>
          )}
          {classFilter !== "all" && (
            <Badge variant="outline" className="bg-indigo-500/10 text-indigo-300 border-indigo-500/30">
              Class: {classFilter}
            </Badge>
          )}
          {debouncedQuery && (
            <Badge variant="outline" className="bg-indigo-500/10 text-indigo-300 border-indigo-500/30">
              Search: "{debouncedQuery}"
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};

export default PNRFilters;
